/**
 * useFeatureAccess Hook
 * Phase 10: Monetization
 *
 * Provides feature access checking based on the current restaurant's subscription plan.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { billingService } from '@/services/billingService';
import { PLAN_LIMITS, getPlanFeatures } from '@/lib/planLimits';
import type {
  PlanId,
  PlanFeatures,
  FeatureKey,
  UsageMetrics,
  UsageLimitResult,
} from '@/types/billing.types';

interface UseFeatureAccessReturn {
  /** Current plan features */
  features: PlanFeatures | null;
  /** Current plan ID */
  planId: PlanId;
  /** Current usage metrics */
  usage: UsageMetrics | null;
  /** Loading state */
  isLoading: boolean;
  /** Whether the user has a premium plan */
  isPremium: boolean;
  /** Check if a feature is available */
  hasFeature: (feature: FeatureKey) => boolean;
  /** Get the limit for a numeric feature */
  getLimit: (feature: FeatureKey) => number;
  /** Check if a feature limit is unlimited */
  isUnlimited: (feature: FeatureKey) => boolean;
  /** Check usage limit for a metric */
  checkUsageLimit: (
    metric: 'products' | 'users' | 'restaurants' | 'ai_requests'
  ) => Promise<UsageLimitResult>;
  /** Refresh feature data */
  refresh: () => Promise<void>;
}

export function useFeatureAccess(): UseFeatureAccessReturn {
  const { currentRestaurantId } = useAuth();
  const [features, setFeatures] = useState<PlanFeatures | null>(null);
  const [planId, setPlanId] = useState<PlanId>('free');
  const [usage, setUsage] = useState<UsageMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadFeatures = useCallback(async () => {
    if (!currentRestaurantId) {
      setFeatures(PLAN_LIMITS.free);
      setPlanId('free');
      setUsage(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const [subscription, usageMetrics] = await Promise.all([
        billingService.getSubscription(currentRestaurantId),
        billingService.getUsageMetrics(currentRestaurantId),
      ]);

      const currentPlanId = subscription?.plan_id || 'free';
      setPlanId(currentPlanId);
      setFeatures(getPlanFeatures(currentPlanId));
      setUsage(usageMetrics);
    } catch (error) {
      console.error('Error loading feature access:', error);
      setFeatures(PLAN_LIMITS.free);
      setPlanId('free');
    } finally {
      setIsLoading(false);
    }
  }, [currentRestaurantId]);

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  /**
   * Check if a feature is available
   */
  const hasFeature = useCallback(
    (feature: FeatureKey): boolean => {
      if (!features) return false;

      const value = features[feature];

      // For boolean features
      if (typeof value === 'boolean') {
        return value;
      }

      // For numeric features (limits), -1 means unlimited, > 0 means available
      if (typeof value === 'number') {
        return value === -1 || value > 0;
      }

      return false;
    },
    [features]
  );

  /**
   * Get the limit for a numeric feature
   */
  const getLimit = useCallback(
    (feature: FeatureKey): number => {
      if (!features) return 0;

      const value = features[feature];

      if (typeof value === 'number') {
        return value;
      }

      // For boolean features, return 1 if true, 0 if false
      if (typeof value === 'boolean') {
        return value ? 1 : 0;
      }

      return 0;
    },
    [features]
  );

  /**
   * Check if a feature limit is unlimited (-1)
   */
  const isUnlimited = useCallback(
    (feature: FeatureKey): boolean => {
      return getLimit(feature) === -1;
    },
    [getLimit]
  );

  /**
   * Check usage limit for a metric
   */
  const checkUsageLimit = useCallback(
    async (
      metric: 'products' | 'users' | 'restaurants' | 'ai_requests'
    ): Promise<UsageLimitResult> => {
      if (!currentRestaurantId) {
        return { allowed: false, current: 0, limit: 0 };
      }

      return billingService.checkUsageLimit(currentRestaurantId, metric);
    },
    [currentRestaurantId]
  );

  const isPremium = planId !== 'free';

  return {
    features,
    planId,
    usage,
    isLoading,
    isPremium,
    hasFeature,
    getLimit,
    isUnlimited,
    checkUsageLimit,
    refresh: loadFeatures,
  };
}

/**
 * Hook to check a single feature
 */
export function useHasFeature(feature: FeatureKey): {
  hasFeature: boolean;
  isLoading: boolean;
} {
  const { hasFeature, isLoading } = useFeatureAccess();

  return {
    hasFeature: hasFeature(feature),
    isLoading,
  };
}

/**
 * Hook to check usage limit for a metric
 */
export function useUsageLimit(
  metric: 'products' | 'users' | 'restaurants' | 'ai_requests'
): {
  allowed: boolean;
  current: number;
  limit: number;
  isUnlimited: boolean;
  isLoading: boolean;
  refresh: () => Promise<void>;
} {
  const { currentRestaurantId } = useAuth();
  const [result, setResult] = useState<UsageLimitResult>({
    allowed: true,
    current: 0,
    limit: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const loadLimit = useCallback(async () => {
    if (!currentRestaurantId) {
      setResult({ allowed: false, current: 0, limit: 0 });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const limitResult = await billingService.checkUsageLimit(
        currentRestaurantId,
        metric
      );
      setResult(limitResult);
    } catch {
      setResult({ allowed: false, current: 0, limit: 0 });
    } finally {
      setIsLoading(false);
    }
  }, [currentRestaurantId, metric]);

  useEffect(() => {
    loadLimit();
  }, [loadLimit]);

  return {
    allowed: result.allowed,
    current: result.current,
    limit: result.limit,
    isUnlimited: result.limit === -1,
    isLoading,
    refresh: loadLimit,
  };
}
